import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowsRightLeftIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useCreateWarehouse,
  useDeleteWarehouse,
  useSetDefaultWarehouse,
  useUpdateWarehouse,
  useWarehousesPage,
  useWarehouseStock,
  useWarehouseSummary,
} from '../../hooks/useWarehouses';
import { Warehouse, WarehouseInput, WarehouseType } from '../../services/warehouse.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const TYPES: { value: WarehouseType; label: string; hint: string }[] = [
  { value: 'main', label: 'Main store', hint: 'The principal store of a site' },
  { value: 'branch', label: 'Branch / sub-store', hint: 'A second store, shop-floor store or branch' },
  { value: 'transit', label: 'Transit', hint: 'Goods on the way' },
  { value: 'virtual', label: 'Virtual', hint: 'A book location, such as stock with a job worker' },
];
const typeLabel = (type: string) => TYPES.find((option) => option.value === type)?.label ?? type;

const CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,19}$/;

interface WarehouseFormProps {
  warehouse: Warehouse | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: WarehouseInput) => void;
}

const WarehouseForm: React.FC<WarehouseFormProps> = ({ warehouse, isPending, onCancel, onSubmit }) => {
  const isEdit = !!warehouse;
  const [form, setForm] = useState({
    code: warehouse?.code ?? '',
    name: warehouse?.name ?? '',
    type: (warehouse?.type ?? 'branch') as WarehouseType,
    address: warehouse?.address ?? '',
    city: warehouse?.city ?? '',
    state: warehouse?.state ?? '',
    pincode: warehouse?.pincode ?? '',
    contactPerson: warehouse?.contactPerson ?? '',
    contactPhone: warehouse?.contactPhone ?? '',
    contactEmail: warehouse?.contactEmail ?? '',
    description: warehouse?.description ?? '',
    isActive: warehouse ? warehouse.isActive : true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const holdsStock = (warehouse?.totalQuantity ?? 0) > 0;

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const code = form.code.trim().toUpperCase();
    if (!CODE_PATTERN.test(code)) next.code = 'Use 2 to 20 capitals, digits or hyphens';
    if (form.name.trim().length < 2) next.name = 'Enter the name of the warehouse';
    if (form.pincode.trim() && !/^[0-9]{6}$/.test(form.pincode.trim())) next.pincode = 'The pincode has 6 digits';
    if (form.contactPhone.trim() && !/^[0-9+()\-\s]{6,20}$/.test(form.contactPhone.trim())) next.contactPhone = 'Enter a valid phone number';
    if (form.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) next.contactEmail = 'Enter a valid email address';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      code,
      name: form.name.trim(),
      type: form.type,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      pincode: form.pincode.trim() || null,
      contactPerson: form.contactPerson.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
      description: form.description.trim() || null,
      isActive: form.isActive,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Warehouse" description="How the store is named on stock documents">
        <Field label="Code" htmlFor="wh-code" required error={errors.code} hint="Short and unique, for example RM-STORE">
          <input
            id="wh-code"
            type="text"
            maxLength={20}
            autoFocus={!isEdit}
            value={form.code}
            onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))}
            className={cn(inputClass, 'uppercase', errors.code && inputErrorClass)}
            placeholder="RM-STORE"
          />
        </Field>
        <Field label="Name" htmlFor="wh-name" required error={errors.name}>
          <input id="wh-name" type="text" maxLength={100} value={form.name} onChange={set('name')} className={cn(inputClass, errors.name && inputErrorClass)} placeholder="Raw Material Store" />
        </Field>
        <Field label="Type" htmlFor="wh-type" hint={TYPES.find((option) => option.value === form.type)?.hint}>
          <select id="wh-type" value={form.type} onChange={set('type')} className={inputClass}>
            {TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Description" htmlFor="wh-description">
          <input id="wh-description" type="text" maxLength={500} value={form.description} onChange={set('description')} className={inputClass} placeholder="What is kept here" />
        </Field>
      </FormCard>

      <FormCard title="Address" description="Optional. Where the store is">
        <Field label="Address" htmlFor="wh-address" className="sm:col-span-2">
          <textarea id="wh-address" rows={2} maxLength={500} value={form.address} onChange={set('address')} className={inputClass} />
        </Field>
        <Field label="City" htmlFor="wh-city">
          <input id="wh-city" type="text" maxLength={100} value={form.city} onChange={set('city')} className={inputClass} />
        </Field>
        <Field label="State" htmlFor="wh-state">
          <input id="wh-state" type="text" maxLength={100} value={form.state} onChange={set('state')} className={inputClass} />
        </Field>
        <Field label="Pincode" htmlFor="wh-pincode" error={errors.pincode}>
          <input id="wh-pincode" type="text" inputMode="numeric" maxLength={6} value={form.pincode} onChange={set('pincode')} className={cn(inputClass, errors.pincode && inputErrorClass)} />
        </Field>
      </FormCard>

      <FormCard title="Contact" description="Optional. Who looks after the store">
        <Field label="Contact person" htmlFor="wh-person">
          <input id="wh-person" type="text" maxLength={100} value={form.contactPerson} onChange={set('contactPerson')} className={inputClass} />
        </Field>
        <Field label="Phone" htmlFor="wh-phone" error={errors.contactPhone}>
          <input id="wh-phone" type="tel" maxLength={20} value={form.contactPhone} onChange={set('contactPhone')} className={cn(inputClass, errors.contactPhone && inputErrorClass)} />
        </Field>
        <Field label="Email" htmlFor="wh-email" error={errors.contactEmail} className="sm:col-span-2">
          <input id="wh-email" type="email" maxLength={150} value={form.contactEmail} onChange={set('contactEmail')} className={cn(inputClass, errors.contactEmail && inputErrorClass)} />
        </Field>
      </FormCard>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={form.isActive}
          disabled={warehouse?.isDefault || holdsStock}
          onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">Active</span>
          <span className="block text-xs text-gray-500">
            {warehouse?.isDefault
              ? 'The default warehouse is always active.'
              : holdsStock
                ? 'This warehouse holds stock. Transfer it out before making the warehouse inactive.'
                : 'Only active warehouses can be picked on stock transfers.'}
          </span>
        </span>
      </label>

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Warehouse'}
        </button>
      </FormActions>
    </form>
  );
};

// What one warehouse holds, with its details
const WarehouseView: React.FC<{ warehouse: Warehouse }> = ({ warehouse }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 8;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching } = useWarehouseStock(warehouse.id, { page, limit, search: search || undefined });
  const rows = data?.data ?? [];
  const place = [warehouse.address, warehouse.city, warehouse.state, warehouse.pincode].filter(Boolean).join(', ');

  return (
    <div className="space-y-5">
      <DetailCard title="Warehouse" columns={4}>
        <DetailItem label="Code">{warehouse.code}</DetailItem>
        <DetailItem label="Type">{typeLabel(warehouse.type)}</DetailItem>
        <DetailItem label="Status">{warehouse.isActive ? 'Active' : 'Inactive'}{warehouse.isDefault ? ' · Default' : ''}</DetailItem>
        <DetailItem label="Stock value">{formatMoney(warehouse.stockValue ?? 0)}</DetailItem>
        <DetailItem label="Address" className="col-span-2">{place || '—'}</DetailItem>
        <DetailItem label="Contact" className="col-span-2">
          {[warehouse.contactPerson, warehouse.contactPhone, warehouse.contactEmail].filter(Boolean).join(' · ') || '—'}
        </DetailItem>
      </DetailCard>

      <section className="rounded-lg border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Stock held here{data ? ` · ${formatNumber(data.total)} product(s)` : ''}
          </h4>
          <div className="relative sm:w-64">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search product or SKU"
              aria-label="Search stock in this warehouse"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
        </div>
        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className={thClass}>Product</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Here</th>
                <th scope="col" className={cn(thClass, 'text-right')}>All warehouses</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Value here</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                    {search ? 'No product here matches.' : 'This warehouse holds no stock.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.productId}>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-medium text-gray-900">{row.name}</p>
                      <p className="text-xs text-gray-500">{row.sku}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">
                      {formatNumber(row.quantity)} <span className="font-normal text-gray-500">{row.unit}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-600 tabular-nums">{formatNumber(row.totalStock)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatMoney(row.value)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {(data?.totalPages ?? 1) > 1 && (
          <div className="flex justify-end border-t border-gray-200 px-4 py-3">
            <Pagination currentPage={page} totalPages={data?.totalPages ?? 1} onPageChange={setPage} />
          </div>
        )}
      </section>
    </div>
  );
};

const Warehouses: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('inventory.create');
  const canUpdate = can('inventory.update');
  const canDelete = can('inventory.delete');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [viewing, setViewing] = useState<Warehouse | null>(null);
  const [deleting, setDeleting] = useState<Warehouse | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useWarehousesPage({
    page,
    limit,
    search: search || undefined,
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
  });
  const { data: summary } = useWarehouseSummary();

  const createWarehouse = useCreateWarehouse();
  const updateWarehouse = useUpdateWarehouse();
  const setDefaultWarehouse = useSetDefaultWarehouse();
  const deleteWarehouse = useDeleteWarehouse();

  const warehouses = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Step back when the last page no longer exists
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter;
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (warehouse: Warehouse) => {
    setEditing(warehouse);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (createWarehouse.isPending || updateWarehouse.isPending) return;
    setFormOpen(false);
  };

  const handleSubmit = (input: WarehouseInput) => {
    const onError = (submitError: unknown) => toast.error(getApiErrorMessage(submitError, 'The warehouse could not be saved'));
    if (editing) {
      updateWarehouse.mutate(
        { id: editing.id, data: input },
        {
          onSuccess: (saved) => {
            toast.success(`${saved.name} updated`);
            setFormOpen(false);
          },
          onError,
        },
      );
    } else {
      createWarehouse.mutate(input, {
        onSuccess: (saved) => {
          toast.success(`${saved.name} added`);
          setFormOpen(false);
        },
        onError,
      });
    }
  };

  const handleSetDefault = (warehouse: Warehouse) => {
    setDefaultWarehouse.mutate(warehouse.id, {
      onSuccess: () => toast.success(`${warehouse.name} is now the default warehouse`),
      onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The default warehouse could not be changed')),
    });
  };

  const handleToggleActive = (warehouse: Warehouse) => {
    updateWarehouse.mutate(
      { id: warehouse.id, data: { isActive: !warehouse.isActive } },
      {
        onSuccess: () => toast.success(`${warehouse.name} marked ${warehouse.isActive ? 'inactive' : 'active'}`),
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The status could not be changed')),
      },
    );
  };

  const handleDelete = () => {
    if (!deleting) return;
    deleteWarehouse.mutate(deleting.id, {
      onSuccess: () => {
        toast.success(`${deleting.name} deleted`);
        setDeleting(null);
      },
      onError: (actionError) => {
        toast.error(getApiErrorMessage(actionError, 'The warehouse could not be deleted'));
        setDeleting(null);
      },
    });
  };

  const stats = [
    { label: 'Warehouses', value: summary ? formatNumber(summary.total) : '—', hint: 'Stores and locations' },
    { label: 'Active', value: summary ? formatNumber(summary.active) : '—', hint: `${summary ? formatNumber(summary.inactive) : '—'} inactive` },
    { label: 'Holding stock', value: summary ? formatNumber(summary.withStock) : '—', hint: 'With at least one unit' },
    { label: 'Default warehouse', value: summary ? summary.defaultWarehouse?.name ?? 'Not set' : '—', hint: 'Used when no warehouse is named' },
  ];

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Warehouses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Where stock is kept. Receipts, issues and production use the default warehouse; stock moves between warehouses with a transfer.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/stock-transfers" className={secondaryButtonClass}>
            <ArrowsRightLeftIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Stock Transfers
          </Link>
          {canCreate && (
            <button type="button" onClick={openAdd} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Add Warehouse
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900" title={String(stat.value)}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by code, name or city"
              aria-label="Search warehouses"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            aria-label="Status"
            className={cn(inputClass, 'lg:w-44')}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The warehouses could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Warehouse</th>
                  <th scope="col" className={thClass}>Type</th>
                  <th scope="col" className={thClass}>Location</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Products</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Units</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Stock value</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 8 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : warehouses.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No warehouse matches' : 'No warehouses yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">{hasFilters ? 'Try a different search or status.' : 'Add the stores your stock is kept in.'}</p>
                    </td>
                  </tr>
                ) : (
                  warehouses.map((warehouse) => (
                    <tr key={warehouse.id} className={cn('hover:bg-gray-50', !warehouse.isActive && 'bg-gray-50/60')}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setViewing(warehouse)} className="text-left text-sm font-medium text-gray-900 hover:text-indigo-600">
                            {warehouse.name}
                          </button>
                          {warehouse.isDefault && <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>Default</span>}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">{warehouse.code}</p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{typeLabel(warehouse.type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {[warehouse.city, warehouse.state].filter(Boolean).join(', ') || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(warehouse.productCount ?? 0)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(warehouse.totalQuantity ?? 0)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(warehouse.stockValue ?? 0)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={cn(badgeClass, warehouse.isActive ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}>
                          {warehouse.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setViewing(warehouse)}
                            title="View stock"
                            aria-label={`View ${warehouse.name}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {canUpdate && !warehouse.isDefault && warehouse.isActive && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(warehouse)}
                              disabled={setDefaultWarehouse.isPending}
                              title="Make default"
                              aria-label={`Make ${warehouse.name} the default`}
                              className="rounded-md p-1.5 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-50"
                            >
                              <StarIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canUpdate && !warehouse.isDefault && (
                            <button
                              type="button"
                              onClick={() => handleToggleActive(warehouse)}
                              disabled={updateWarehouse.isPending}
                              className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                              {warehouse.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              type="button"
                              onClick={() => openEdit(warehouse)}
                              title="Edit"
                              aria-label={`Edit ${warehouse.name}`}
                              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => setDeleting(warehouse)}
                              disabled={warehouse.isDefault}
                              title={warehouse.isDefault ? 'The default warehouse cannot be deleted' : 'Delete'}
                              aria-label={`Delete ${warehouse.name}`}
                              className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${editing.name}` : 'Add Warehouse'}
        description={editing ? 'Stock already in the warehouse is not affected.' : 'A new warehouse starts empty. Move stock into it with a transfer.'}
        size="xl"
      >
        {formOpen && (
          <WarehouseForm
            key={editing?.id ?? 'new'}
            warehouse={editing}
            isPending={createWarehouse.isPending || updateWarehouse.isPending}
            onCancel={closeForm}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.name ?? ''} description={viewing?.description ?? undefined} size="2xl">
        {viewing && <WarehouseView key={viewing.id} warehouse={viewing} />}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete warehouse"
        message={
          deleting ? (
            <>
              Delete <span className="font-semibold">{deleting.name}</span> ({deleting.code})? Only a warehouse that never held stock can be
              deleted; one with stock history is made inactive instead.
            </>
          ) : null
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={deleteWarehouse.isPending}
      />
    </div>
  );
};

export default Warehouses;
